var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Box, Button, CircularProgress } from '@mui/material';
import React from 'react';
import { useCommonWebTranslation } from '../../hooks';
import { walkTreeItems } from '../utils';
import { TreeItemDivider } from './TreeItemDivider';
import { TreeItemHoverContent } from './TreeItemHoverContent';
export const TreeItemSelectionButtons = ({ item, size, focus, selected, allSelected, onChangeSelectedItemIds, onSelectAllAsync, onDeselectAllAsync, }) => {
    const { t } = useCommonWebTranslation();
    const hasChildren = item.children != null && item.children.length > 0;
    const [refocusFlag, setRefocusFlag] = React.useState();
    const [refocusAllFlag, setRefocusAllFlag] = React.useState();
    const [loadingButton, setLoadingButton] = React.useState(false);
    const singleSelectButtonRef = React.useRef(null);
    const multipleSelectButtonRef = React.useRef(null);
    const handleSelectionClick = (multiSelect) => {
        if (multiSelect) {
            setRefocusAllFlag({});
        }
        else {
            setRefocusFlag({});
        }
    };
    const getSpinnerSize = () => {
        switch (size) {
            case 'small':
                return 18;
            case 'large':
                return 24;
            case 'medium':
            default:
                return 20;
        }
    };
    // When unmounting a button we also lose focus on it. We need to make a new button, which is mounted in the same spot,
    // focused in order to preserve location where keyboard users were last located.
    // One effect for each group of buttons
    React.useEffect(() => {
        var _a;
        if (focus) {
            if (refocusFlag) {
                (_a = singleSelectButtonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        }
        else {
            // when blurring the current tree row, reset refocus state
            if (refocusFlag) {
                setRefocusFlag(null);
            }
        }
    }, [focus, refocusFlag]);
    React.useEffect(() => {
        var _a;
        if (focus) {
            if (refocusAllFlag) {
                (_a = multipleSelectButtonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        }
        else {
            if (refocusAllFlag) {
                setRefocusAllFlag(null);
            }
        }
    }, [focus, refocusAllFlag]);
    return (React.createElement(React.Fragment, null,
        hasChildren && (React.createElement(React.Fragment, null,
            React.createElement(TreeItemHoverContent, { sx: { display: focus ? 'block' : 'none' } },
                React.createElement(Box, { sx: { flexShrink: 0, display: 'flex', alignItems: 'center', gap: size === 'small' ? '4px' : '8px' } },
                    allSelected && (React.createElement(Button, { ref: multipleSelectButtonRef, size: size, disabled: loadingButton, tabIndex: focus ? 0 : -1, onClick: (event) => __awaiter(void 0, void 0, void 0, function* () {
                            var _a;
                            event.stopPropagation();
                            let ids = [];
                            if (onDeselectAllAsync) {
                                setLoadingButton(true);
                                ids = yield onDeselectAllAsync(item.id);
                                setLoadingButton(false);
                            }
                            else {
                                ids = [item.id];
                                walkTreeItems((_a = item.children) !== null && _a !== void 0 ? _a : [], (child) => ids.push(child.id));
                            }
                            onChangeSelectedItemIds === null || onChangeSelectedItemIds === void 0 ? void 0 : onChangeSelectedItemIds((prev) => (prev !== null && prev !== void 0 ? prev : []).filter((id) => !ids.includes(id)));
                            handleSelectionClick(true);
                        }), sx: { flexShrink: 0, whiteSpace: 'nowrap' }, "data-test": 'deselect-all-button' }, loadingButton ? React.createElement(CircularProgress, { size: getSpinnerSize() }) : t('common.deselectAll'))),
                    !allSelected && (React.createElement(Button, { ref: multipleSelectButtonRef, size: size, disabled: loadingButton, tabIndex: focus ? 0 : -1, onClick: (event) => __awaiter(void 0, void 0, void 0, function* () {
                            event.stopPropagation();
                            let asyncIds = [];
                            if (onSelectAllAsync) {
                                setLoadingButton(true);
                                asyncIds = yield onSelectAllAsync(item.id);
                                setLoadingButton(false);
                            }
                            onChangeSelectedItemIds === null || onChangeSelectedItemIds === void 0 ? void 0 : onChangeSelectedItemIds((prev) => {
                                var _a;
                                const ids = [...(prev !== null && prev !== void 0 ? prev : [])];
                                if (onSelectAllAsync) {
                                    ids.push(...asyncIds);
                                }
                                else {
                                    if (!ids.includes(item.id)) {
                                        ids.push(item.id);
                                    }
                                    walkTreeItems((_a = item.children) !== null && _a !== void 0 ? _a : [], (child) => {
                                        if (!ids.includes(child.id) && !child.disabled) {
                                            ids.push(child.id);
                                        }
                                    });
                                }
                                handleSelectionClick(true);
                                return ids;
                            });
                        }), sx: { flexShrink: 0, whiteSpace: 'nowrap' }, "data-test": 'select-all-button' }, loadingButton ? React.createElement(CircularProgress, { size: getSpinnerSize() }) : t('common.selectAll'))),
                    React.createElement(TreeItemDivider, null))))),
        selected && (React.createElement(Button, { ref: singleSelectButtonRef, size: size, tabIndex: focus ? 0 : -1, onClick: (event) => {
                event.stopPropagation();
                onChangeSelectedItemIds === null || onChangeSelectedItemIds === void 0 ? void 0 : onChangeSelectedItemIds((prev) => (prev !== null && prev !== void 0 ? prev : []).filter((id) => id !== item.id));
                handleSelectionClick();
            }, sx: { flexShrink: 0, whiteSpace: 'nowrap' }, "data-test": 'deselect-button' }, t('common.deselect'))),
        !selected && (React.createElement(Button, { ref: singleSelectButtonRef, size: size, tabIndex: focus ? 0 : -1, onClick: (event) => {
                event.stopPropagation();
                onChangeSelectedItemIds === null || onChangeSelectedItemIds === void 0 ? void 0 : onChangeSelectedItemIds((prev) => [...(prev !== null && prev !== void 0 ? prev : []), item.id]);
                handleSelectionClick();
            }, sx: { flexShrink: 0, whiteSpace: 'nowrap' }, "data-test": 'select-button' }, t('common.select')))));
};
